import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customers } from '@/api/customers';
import { useAuth } from '@/lib/auth-store';
import { useCustomerContext } from '@/lib/customer-context-store';
import { formatRef } from '@/lib/formatRef';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/**
 * CustomerPicker — rendered inside create dialogs for resources that
 * MUST be tied to a customer (voicemail boxes, products, surveys, etc).
 *
 * Resolution order for the effective customer id:
 *   1. customer-context-store.current.id  (super_admin / reseller_owner
 *      have "entered" a customer from the customers list — the entire
 *      portal is operating in that scope)
 *   2. auth-store.tenant.customerId       (office_manager / standard_user /
 *      limited_user — their own tenant)
 *   3. the local `value` prop (legacy path — a dialog is still asking the
 *      user to pick a customer inline because context isn't set yet)
 *
 * Behavior by role, WITH a customer context entered:
 *   Every role: returns { effectiveCustomerId: context.id, element: null,
 *   isPickerVisible: false }. The dialog doesn't need to render the
 *   picker — the banner already tells the user which customer is active.
 *
 * Behavior by role, WITHOUT a customer context entered (legacy path):
 *   - office_manager / standard_user / limited_user: returns effective id
 *     from tenant scope, element is null.
 *   - reseller_owner / super_admin: renders a select listing their
 *     customers (the original behavior). Dialogs that render this picker
 *     still work unchanged.
 *
 * Parent dialogs should:
 *   1. Render {customerPicker} (null when a context is set — cheap no-op)
 *   2. Disable submit while `effectiveCustomerId` is null.
 *   3. Pass the selected id as `customer_id` in the create body when
 *      `isPickerVisible` was true. When `isPickerVisible` is false and
 *      effectiveCustomerId comes from context or tenant, the backend
 *      either reads the context (future: from the session) or skips the
 *      field entirely (today: backend infers from tenant scope).
 */

export interface UseCustomerPickerResult {
  /** null when no customer is available / selected yet. */
  effectiveCustomerId: string | null;
  /** null when no picker is rendered (context set, or role doesn't need one). */
  element: React.ReactNode;
  /** true when a picker is actually rendered (role needs it, no context). */
  isPickerVisible: boolean;
}

export function useCustomerPicker(
  value: string | null,
  onChange: (customerId: string | null) => void,
  opts: { label?: string; required?: boolean } = {},
): UseCustomerPickerResult {
  const tenant = useAuth((s) => s.tenant);
  const user = useAuth((s) => s.user);
  const contextCustomerId = useCustomerContext((s) => s.current?.id ?? null);

  const needsPicker = !!user && (user.role === 'super_admin' || user.role === 'reseller_owner');
  const tenantCustomerId = tenant?.customerId ?? null;

  // Only fire the customers-list query when the legacy picker path is
  // actually going to render. Context + non-admin roles short-circuit
  // before the element below is ever used, so the query stays idle.
  const shouldFetch = needsPicker && !contextCustomerId;

  const { data, isLoading } = useQuery({
    queryKey: ['customers-picker'],
    queryFn: () => customers.list({ limit: 500 }),
    enabled: shouldFetch,
    staleTime: 30_000,
  });

  const rows = useMemo(() => {
    const list = data?.rows ?? [];
    return list.filter((c) => c.active).sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // New pattern — if the user has entered a customer via the customers
  // list, the entire portal operates in that scope. No picker needed.
  if (contextCustomerId) {
    return {
      effectiveCustomerId: contextCustomerId,
      element: null,
      isPickerVisible: false,
    };
  }

  // Non-admin roles inherit their own tenant's customer. Picker is never
  // shown here because the role literally has one customer in scope.
  if (!needsPicker) {
    return {
      effectiveCustomerId: tenantCustomerId,
      element: null,
      isPickerVisible: false,
    };
  }

  // Legacy fallback — super_admin / reseller_owner without a customer
  // context, using a dialog that still asks them to pick inline. These
  // call sites are being migrated away from over time.
  const label = opts.label ?? 'Customer';
  const element = (
    <div className="space-y-1">
      <Label>
        {label}
        {opts.required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <Select
        value={value ?? ''}
        onValueChange={(v) => onChange(v || null)}
        disabled={isLoading || rows.length === 0}
      >
        <SelectTrigger>
          <SelectValue placeholder={
            isLoading ? 'Loading customers…'
              : rows.length === 0 ? 'No customers in scope'
                : 'Select a customer…'
          } />
        </SelectTrigger>
        <SelectContent>
          {rows.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              <span className="flex items-baseline gap-2">
                <span className="text-sm">{c.name}</span>
                <span className="font-mono text-[10px] text-slate-400">
                  {formatRef(c.customer_number, 'C')}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {rows.length === 0 && !isLoading && (
        <p className="text-[11px] text-muted-foreground">
          No active customers in your scope. Create one first.
        </p>
      )}
    </div>
  );

  return {
    effectiveCustomerId: value,
    element,
    isPickerVisible: true,
  };
}
